"use client"

import * as React from "react"
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, XAxis, YAxis } from "recharts"
import { KeyMetrics } from "@/components/key-metrics"
import { LocalBanner } from "@/components/ui/banner"
import { ChartDataTable, ChartFigure, ChartCard } from "@/components/charts-core"
import {
  ChartContainer,
  ChartTooltip,
  chartTooltipKeyboardSyncProps,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart"
import { CHART_KBD_ACTIVE_BAR } from "@/lib/chart-keyboard-selection"
import { CHART_AXIS_TICK } from "@/lib/chart-typography"
import { DashboardSectionTitle } from "@/components/dashboard-section-heading"
import { EvaluationAnalyticFocusTerm } from "./evaluation-analytic-focus-term"
import { PastTermsTable } from "./past-terms-table"
import {
  evaluationAnalyticsApproachKpiMetrics,
  formatAnalyticVerdict,
  mergeTermHistory,
  buildTermResponseChartData,
  buildTermScoreChartData,
} from "@/lib/mock/evaluation-dashboard-kpi"
import { setEvaluationDashboardApproach } from "@/hooks/use-evaluation-dashboard-approach"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"
import { EVAL_DASHBOARD_KPI_STRIP_CLASS } from "@/lib/evaluation-dashboard-a11y"

const RESPONSE_TARGET = 70

const RESPONSE_CFG: ChartConfig = {
  rate: { label: "Response rate", color: "var(--color-chart-1)" },
  target: { label: "Target", color: "var(--color-chart-4)" },
}

const SCORE_CFG: ChartConfig = {
  score: { label: "Program average", color: "var(--color-chart-2)" },
}

interface EvaluationDashboardAnalyticViewProps {
  currentTerm?: EvaluationTerm
  lastTerm?: EvaluationTerm
  pastTerms: EvaluationTerm[]
}

export function EvaluationDashboardAnalyticView({
  currentTerm,
  lastTerm,
  pastTerms,
}: EvaluationDashboardAnalyticViewProps) {
  const focusTerm = lastTerm ?? pastTerms.at(-1)
  const historyTerms = React.useMemo(
    () => mergeTermHistory(lastTerm, pastTerms),
    [lastTerm, pastTerms],
  )

  const metrics = React.useMemo(
    () => evaluationAnalyticsApproachKpiMetrics(focusTerm, pastTerms),
    [focusTerm, pastTerms],
  )

  const responseData = React.useMemo(() => buildTermResponseChartData(), [])
  const scoreData = React.useMemo(() => buildTermScoreChartData(), [])

  const showLiveBanner =
    currentTerm != null &&
    (currentTerm.counts.live > 0 || currentTerm.counts.scheduled > 0)

  return (
    <div className="flex flex-col gap-10">
      <section aria-labelledby="eval-analytic-summary" className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <DashboardSectionTitle as="h2" id="eval-analytic-summary">
            Program quality summary
          </DashboardSectionTitle>
          {focusTerm ? (
            <p className="text-sm text-muted-foreground">{formatAnalyticVerdict(focusTerm)}</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Closed term results will appear here after evaluations finish.
            </p>
          )}
        </div>

        <KeyMetrics
          variant="cards"
          size="md"
          metrics={metrics}
          showHeader={false}
          metricsSingleRow
          className={EVAL_DASHBOARD_KPI_STRIP_CLASS}
        />
      </section>

      {showLiveBanner && currentTerm ? (
        <LocalBanner
          variant="info"
          title={`${currentTerm.termName} evaluations in progress`}
          action={{
            label: "Open lifecycle view",
            onClick: () => setEvaluationDashboardApproach("lifecycle"),
          }}
        >
          {currentTerm.liveResponseRate != null
            ? `${currentTerm.counts.live} live · ${currentTerm.liveResponseRate}% response so far.`
            : `${currentTerm.counts.live} live evaluations running.`}
        </LocalBanner>
      ) : null}

      {focusTerm ? (
        <section aria-labelledby="eval-analytic-focus" className="flex flex-col gap-4">
          <DashboardSectionTitle as="h2" id="eval-analytic-focus">
            Last closed term
          </DashboardSectionTitle>
          <EvaluationAnalyticFocusTerm term={focusTerm} />
        </section>
      ) : null}

      <section aria-labelledby="eval-analytic-trends" className="flex flex-col gap-4">
        <DashboardSectionTitle as="h2" id="eval-analytic-trends">
          Trends
        </DashboardSectionTitle>

        <ChartCard title="Response rate by term" description="Closed terms and live window">
          <ChartFigure
            label="Response rate by term"
            summary={
              responseData.length > 0
                ? responseData.map(row => `${row.name} ${row.rate}%`).join(", ")
                : "No closed terms yet."
            }
            dataLength={responseData.length}
          >
            {activeIndex =>
              responseData.length > 0 ? (
                <>
                  <ChartContainer config={RESPONSE_CFG} className="h-[240px] w-full">
                    <BarChart data={responseData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={CHART_AXIS_TICK} />
                      <YAxis domain={[0, 100]} tick={CHART_AXIS_TICK} width={36} />
                      <ReferenceLine
                        y={RESPONSE_TARGET}
                        stroke="var(--color-chart-4)"
                        strokeDasharray="4 4"
                        label={{
                          value: `Target ${RESPONSE_TARGET}%`,
                          position: "insideTopRight",
                          fill: "var(--muted-foreground)",
                          fontSize: 11,
                        }}
                      />
                      <ChartTooltip
                        key={chartTooltipKeyboardSyncProps(activeIndex).key}
                        {...chartTooltipKeyboardSyncProps(activeIndex).props}
                        content={<ChartTooltipContent />}
                      />
                      <Bar dataKey="rate" radius={[4, 4, 0, 0]} activeBar={CHART_KBD_ACTIVE_BAR}>
                        {responseData.map((entry, index) => (
                          <Cell
                            key={entry.name}
                            fill={
                              index === activeIndex
                                ? "var(--color-chart-1)"
                                : "color-mix(in oklch, var(--color-chart-1) 72%, transparent)"
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                  <ChartDataTable
                    caption="Response rate by term"
                    headers={["Term", "Rate"]}
                    rows={responseData.map(row => [row.name, `${row.rate}%`])}
                  />
                </>
              ) : (
                <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
                  No closed terms yet.
                </div>
              )
            }
          </ChartFigure>
        </ChartCard>

        <ChartCard title="Program average by term" description="Last six closed terms">
          <ChartFigure
            label="Program average by term"
            summary={
              scoreData.length > 0
                ? scoreData.map(row => `${row.name} ${row.score.toFixed(1)}`).join(", ")
                : "No closed terms yet."
            }
            dataLength={scoreData.length}
          >
            {activeIndex =>
              scoreData.length > 0 ? (
                <>
                  <ChartContainer config={SCORE_CFG} className="h-[240px] w-full">
                    <BarChart data={scoreData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={CHART_AXIS_TICK} />
                      <YAxis domain={[0, 5]} tick={CHART_AXIS_TICK} width={32} />
                      <ChartTooltip
                        key={chartTooltipKeyboardSyncProps(activeIndex).key}
                        {...chartTooltipKeyboardSyncProps(activeIndex).props}
                        content={<ChartTooltipContent />}
                      />
                      <Bar dataKey="score" radius={[4, 4, 0, 0]} activeBar={CHART_KBD_ACTIVE_BAR}>
                        {scoreData.map((entry, index) => (
                          <Cell
                            key={entry.name}
                            fill={
                              index === activeIndex
                                ? "var(--color-chart-2)"
                                : "color-mix(in oklch, var(--color-chart-2) 72%, transparent)"
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                  <ChartDataTable
                    caption="Program average by term"
                    headers={["Term", "Average"]}
                    rows={scoreData.map(row => [row.name, row.score.toFixed(1)])}
                  />
                </>
              ) : (
                <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
                  No closed terms yet.
                </div>
              )
            }
          </ChartFigure>
        </ChartCard>
      </section>

      <section
        id="eval-term-history"
        aria-labelledby="eval-analytic-history"
        className="flex flex-col gap-4"
      >
        <DashboardSectionTitle as="h2" id="eval-analytic-history">
          Term history
        </DashboardSectionTitle>
        {historyTerms.length > 0 ? (
          <PastTermsTable terms={historyTerms} />
        ) : (
          <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
            No closed terms yet.
          </div>
        )}
      </section>
    </div>
  )
}
